import { Injectable } from '@angular/core';
import { AuthService } from '../../../auth/auth.service';
import { UserService } from '../../../operator/user-service/user.service';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';
import { MultiBuyerCmp } from '../../interfaces/company.interface';
import { CompanyService } from '../company/company.service';
import { MultiBuyerStateService } from './multi-buyer-state.service';

export class NullMultiBuyerCmp implements MultiBuyerCmp {
  readonly id = 0;
  readonly userId = 0;
  readonly nm = '';
  readonly apiUserId = 0;
}

@Injectable({
  providedIn: 'root',
})
export class MultiBuyerSession {
  private _pendingSelection: Promise<MultiBuyerCmp> | null = null;
  private readonly _nullBuyer = new NullMultiBuyerCmp();

  constructor(
    private readonly _userService: UserService,
    private readonly _companyService: CompanyService,
    private readonly _auth: AuthService,
    private readonly _multiBuyerState: MultiBuyerStateService
  ) {
    this._multiBuyerState.loggedInAsMultiBuyer$
      .pipe(
        distinctUntilChanged(),
        filter((status) => this._ready(status))
      )
      .subscribe(() => {
        this.selectPrimaryEntityPromise();
      });
  }

  private _ready(status: boolean) {
    return (
      !!status &&
      !!this._auth.user &&
      this._multiBuyerState.accessControlConfig.accessibleEntities.length > 0
    );
  }

  public async buyers(
    cmpId: number,
    buyerCompanyId?: number
  ): Promise<MultiBuyerCmp[]> {
    const userRole = this._multiBuyerState.accessControlConfig.userRole;
    const allBuyers =
      (await this._companyService
        .getMultiBuyerCmpsList(cmpId, buyerCompanyId, userRole)
        .pipe(take(1))
        .toPromise()) ?? [];

    const accessibleIds = this._multiBuyerState.multiBuyerCmpsIds;
    if (!accessibleIds.length) {
      return allBuyers;
    }
    return allBuyers.filter((b) => accessibleIds.includes(b.id));
  }

  public async selectBuyer(
    cmpId: number,
    buyerCompanyId: number
  ): Promise<MultiBuyerCmp> {
    const buyers = await this.buyers(cmpId, buyerCompanyId);

    if (buyers.length === 0) {
      throw new Error('No buyer company found for the provided IDs.');
    }

    const selectedCompany = buyers.find((item) => item.id === buyerCompanyId);

    if (!selectedCompany) {
      throw new Error(
        `Buyer company ${buyerCompanyId} not found in the returned list.`
      );
    }

    const tokenResponse = await this._userService
      .getToken(selectedCompany.apiUserId, false)
      .toPromise();

    if (tokenResponse) {
      const multiBuyerCmpAuth = {
        ...tokenResponse,
        ...selectedCompany,
        parentCompany: cmpId,
      };

      await this._auth.setSelectedMultiBuyerCompanyData(
        multiBuyerCmpAuth,
        selectedCompany.id
      );
    }
    return selectedCompany;
  }

  public resetBuyerToken(): void {
    this._auth.resetMultiBuyerToken();
  }

  public primaryEntityPending(): boolean {
    return !!this._pendingSelection;
  }

  public async selectPrimaryEntityPromise(): Promise<MultiBuyerCmp> {
    if (
      !this._auth.isLoggedInAsMultiBuyer() ||
      !!this._multiBuyerState.selectedMultiBuyerCompany
    ) {
      return this._nullBuyer;
    }

    if (this._pendingSelection) {
      return this._pendingSelection;
    }

    const user = this._auth.user;
    const primaryEntityId = user?.primary_entity_id;

    if (!primaryEntityId) {
      return this._nullBuyer;
    }

    const accessibleIds =
      this._multiBuyerState.accessControlConfig.accessibleEntities.map(
        (entity) => entity.id
      );

    if (!accessibleIds.includes(primaryEntityId)) {
      return this._nullBuyer;
    }

    this._pendingSelection = this.selectBuyer(user.cmp, primaryEntityId)
      .catch((err) => {
        console.error('Primary entity selection failed:', err);
        return this._nullBuyer;
      })
      .finally(() => {
        this._pendingSelection = null;
        if (!this._multiBuyerState.selectedMultiBuyerCompany) {
          this._multiBuyerState.setSelectedCompany(undefined);
        }
      });

    return this._pendingSelection;
  }
}
